import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { useAppSelector } from "@repo/store/hooks";

// ⚠️ Temporary solution, should get it on packages/ui/src/types/user -> UserRole as enum
// ⚠️ Updated to use AuthUXContext (Middleware Verified) instead of Redux state
import { useAuthUX } from "../context/AuthUXContext";

export interface AuthUser {
  username?: string;
  userRole: string;
  firstName: string;
  lastName: string;
  email: string | undefined;
  userKey: string;
  organization?: string;
  organizationType?: string;
  programs?: unknown;
}

export interface Programs {
  programCode?: string;
  programLevel?: string;
}
export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const logoutUser = createAsyncThunk("user/logoutUser", async () => {});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<AuthUser>) => {
      state.loading = false;
      state.user = {
        username: action.payload.username,
        userRole: action.payload.userRole,
        userKey: action.payload.userKey,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        organization: action.payload.organization,
        organizationType: action.payload.organizationType,
        programs: action.payload.programs,
      };
      state.isAuthenticated = true;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    setAuth: (state, action: PayloadAction<AuthUser>) => {
      state.loading = false;
      state.user = {
        username: action.payload.username,
        userRole: action.payload.userRole,
        userKey: action.payload.userKey,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        email: action.payload.email,
        organization: action.payload.organization,
        organizationType: action.payload.organizationType,
        programs: action.payload.programs,
      };
      state.isAuthenticated = true;
    },
  },
});

// Hooks
export const useAuthState = () => useAppSelector((state) => state.auth);

export const useIsUserStaff = () => {
  const { isStaff } = useAuthUX();
  return isStaff;
};

export const useIsUserMember = () => {
  const { userRole } = useAuthUX();
  const authUserRole = useAppSelector((state) => state.auth.user?.userRole);
  const effectiveRole = userRole || authUserRole;
  return effectiveRole === "WWWM";
};

export const useIsUserStaffOrMember = () => {
  const { isStaff, userRole } = useAuthUX();
  const authUserRole = useAppSelector((state) => state.auth.user?.userRole);
  const effectiveRole = userRole || authUserRole;
  // Check for Staff (EMPL) or Member (WWWM)
  // TODO: Temporary fix due to release impact, role code for member should be WWWM
  // Analyze why we get member as the role code and fix it properly
  return isStaff || effectiveRole === "WWWM" || effectiveRole === "member";
};

export const { loginStart, loginSuccess, loginFailure, logout, setAuth } = authSlice.actions;
export default authSlice.reducer;
